// Paris: the four days, and how far off each one you are. 649 km on one line, split into its
// days, tap one to open it: distance and climb against your longest and biggest, the steepest
// half-kilometre, the modelled time, the light on the day. What is still open, said plainly.
import { useEffect, useState } from "preact/hooks";
import { useMusette } from "../lib/store";
import { dateLabel, daysBetween, todayIso, weeksTo } from "../lib/dates";
import { longest, biggestClimb } from "../lib/analysis";
import { sunTimes, hhmm } from "../lib/sun";
import { movingMin } from "../lib/ride";
import { Head } from "../components/Head";
import { RouteMap } from "../components/RouteMap";
import { Profile } from "../components/Profile";
import type { Track } from "../data/paris-tracks";
import { photo } from "../data/photos";
import { fmtDur } from "./Today";
import { homeOf } from "../data/empty";

export function Paris() {
  const s = useMusette();
  const [tracks, setTracks] = useState<Track[] | null>(null);
  useEffect(() => { import("../data/paris-tracks").then((m) => setTracks(m.PARIS_TRACKS)); }, []);
  const t = todayIso();
  const L = longest(s.rides)?.km ?? 0;
  const C = biggestClimb(s.rides)?.m ?? 0;
  const total = s.stages.reduce((a, st) => a + (st.km.v ?? 0), 0);
  const climb = s.stages.reduce((a, st) => a + (st.gain.v ?? 0), 0);
  const [day, setDay] = useState(1);
  const st = s.stages.find((x) => x.day === day) ?? s.stages[0];
  const km = st.km.v ?? 0, gain = st.gain.v ?? 0;
  const sun = sunTimes(homeOf(s).lat, homeOf(s).lon, st.date);
  const weeks = weeksTo(t, s.stages[0].date);

  return (
    <main class="screen signal">
      <Head right={`${weeks} weeks`} />
      <section class="stack" style="margin-top: var(--space-3)">
        <div class="card photo-card tall">
          <img src={photo("paris")} alt="" decoding="async" />
          <div class="photo-cap">
            <span class="label" style="color:#fff">12 to 15 June 2027</span>
            <span class="say" style="color:#fff">Manchester to Paris</span>
            <span style="color:rgba(255,255,255,.85)">{total} km in four days · {climb.toLocaleString("en-GB")} m of climbing</span>
          </div>
        </div>

        <div class="card">
          <div class="row-between"><span class="label ink">The four days</span><span class="label">Tap a day</span></div>
          <div class="days-line" role="radiogroup" aria-label="The four days">
            {s.stages.map((x) => (
              <button type="button" role="radio" aria-checked={x.day === day} class={x.day === day ? "on" : ""} style={`flex:${x.km.v ?? 1}`} onClick={() => setDay(x.day)}>
                <span class="label">Day {x.day}</span><span class="dl-km">{x.km.v}</span>
              </button>
            ))}
          </div>
          <div class="day-detail">
            <span class="label amber">Day {st.day} · {dateLabel(st.date)}</span>
            <span class="say">{st.from} to {st.to}</span>
            <div class="stats">
              <div class="card stat"><span class="label">Distance</span><span class="stat-n">{km}<small> km</small></span></div>
              <div class="card stat"><span class="label">Climb</span><span class="stat-n">{gain.toLocaleString("en-GB")}<small> m</small></span></div>
              <div class="card stat"><span class="label">Moving</span><span class="stat-n">{fmtDur(movingMin(km, gain)).replace(" h ", ":")}</span></div>
            </div>
            {tracks?.[st.day - 1] && (
              <>
                <RouteMap points={tracks[st.day - 1].track} label={`Map of Day ${st.day}, ${st.from} to ${st.to}`} h={200} tiles />
                <Profile points={tracks[st.day - 1].profile} label={`Height profile of Day ${st.day}`} />
              </>
            )}
            <div class="row-between"><span class="label">Your longest</span><span class="label">{L} of {km} km</span></div>
            <div class="track thin"><span class="track-fill" style={`width:${Math.min(100, (L / km) * 100)}%`} /></div>
            <div class="row-between"><span class="label">Your biggest climb</span><span class="label">{C} of {gain.toLocaleString("en-GB")} m</span></div>
            <div class="track thin"><span class="track-fill" style={`width:${Math.min(100, (C / gain) * 100)}%`} /></div>
            <span class="soft">Steepest half-kilometre {st.steep.v}%. Moving time from your pace model, without stops. Sunrise {hhmm(sun.rise)}, sunset {hhmm(sun.set)} at home that day, computed.</span>
          </div>
        </div>

        <div class="card">
          <span class="label ink">Still open</span>
          <div class="open-list">
            <span>Where Day 1 starts: the file begins 4.5 km east of Meadow Mill.</span>
            <span>The Wellingborough overnight.</span>
          </div>
          <a class="text-btn" href="/coach">Ask the coach</a>
        </div>
        <span class="label">Measured from your three Komoot files on 24 October. Days 1 and 2 split where the line passes 4 km from Wellingborough.</span>
      </section>
    </main>
  );
}

export function NotYet({ title, line }: { title: string; line: string; photos?: string[] }) {
  return (
    <main class="screen signal">
      <Head right={title} />
      <section class="stack" style="margin-top: var(--space-6)">
        <span class="say">{line}</span>
        <a class="text-btn" href="/">Back to Today</a>
      </section>
    </main>
  );
}
